/**
 * @techstack/core - Quote pricing engine
 *
 * Pure functions: (lines, scenario) → economics. No I/O, no Date.now(),
 * no randomness — the waterfall math lives here and ONLY here, shared
 * verbatim by the GAS app and TekStak (DESIGN-CODE-WORKFLOW: contracts
 * are law; charts/components are presentational).
 *
 * Money handling: unit prices are rounded to cents before extending
 * (unit × qty), and each tier of the waterfall is rounded to cents —
 * matching how the numbers land on an exported sheet, so the UI, the
 * export, and the engine never disagree by a penny.
 */

import type {
  LineEconomics,
  PriceResolution,
  QuoteEconomics,
  QuoteLineItem,
  QuoteScenario,
  TcoRoll,
} from '../contracts/quoting';

/**
 * Round to cents, half-up — matching spreadsheet ROUND() so the UI,
 * engine, and exported sheet agree. toPrecision(15) strips float
 * representation error first (112.575 × 100 = 11257.499999999998 would
 * otherwise round DOWN; a flat Number.EPSILON nudge is too small at
 * this magnitude).
 */
export function round2(n: number): number {
  return Math.round(Number((n * 100).toPrecision(15))) / 100;
}

/** Valid fraction for discounts/margins: 0 ≤ x < 1. */
function assertPct(value: number, field: string): void {
  if (!Number.isFinite(value) || value < 0 || value >= 1) {
    throw new RangeError(`${field} must be a fraction in [0, 1), got ${value}`);
  }
}

/** Throws RangeError when a scenario's levers are out of range. */
export function validateScenario(scenario: QuoteScenario): void {
  assertPct(scenario.customerDiscountPct, 'customerDiscountPct');
  assertPct(scenario.partnerMarginPct, 'partnerMarginPct');
  assertPct(scenario.distiMarginPct, 'distiMarginPct');
  if (scenario.productOffListPct != null) assertPct(scenario.productOffListPct, 'productOffListPct');
  if (scenario.servicesOffListPct != null) assertPct(scenario.servicesOffListPct, 'servicesOffListPct');
  if (scenario.panDealRegPct !== undefined) assertPct(scenario.panDealRegPct, 'panDealRegPct');
  if (scenario.financeRatePct !== undefined) assertPct(scenario.financeRatePct, 'financeRatePct');
  if (scenario.financeYears !== undefined
      && (!Number.isInteger(scenario.financeYears) || scenario.financeYears < 0 || scenario.financeYears > 10)) {
    throw new RangeError(`financeYears must be an integer 0–10, got ${scenario.financeYears}`);
  }
}

/** Organizer rows (group headers + sections) — no pricing, excluded
 *  from all economics. */
export function isOrganizerLine(l: Pick<QuoteLineItem, 'kind'>): boolean {
  return l.kind === 'header' || l.kind === 'section';
}

/**
 * Per-line ON/OFF resolution for toggleable organizers, two levels:
 * a SECTION row's `enabled` gates everything until the next section
 * (its groups and lines); a HEADER row's `enabled` gates its group
 * (the lines after it, until the next organizer); a line's own
 * `enabled` switches just that line. A new section resets the group
 * gate — groups never span sections. Rows before the first organizer
 * are ungrouped and default on. Returns one boolean per input line
 * (organizers get their own flag: header AND its section).
 *
 * The single source of truth for what's "in" the quote — the engine,
 * the UI dimming, and every export must agree. Mirrored in
 * apps/backend/services/quotes.js (list SQL) and apps/gas/src/Code.js
 * qbSegmentEnabled_ — keep all three in step.
 */
export function segmentEnabled(lines: QuoteLineItem[]): boolean[] {
  let sectionOn = true;
  let groupOn = true;
  return lines.map((l) => {
    if (l.kind === 'section') {
      sectionOn = l.enabled !== false;
      groupOn = true;
      return sectionOn;
    }
    if (l.kind === 'header') {
      groupOn = l.enabled !== false;
      return sectionOn && groupOn;
    }
    return sectionOn && groupOn && l.enabled !== false;
  });
}

/**
 * Resolve unit prices from raw pricing signals, in resolution order:
 *   1. explicit contract price
 *   2. per-SKU stated discount off MSRP
 *   3. category discount rule off MSRP
 *   4. list-only (no contract price)
 *
 * Used when ADDING a line (snapshotting gpl_current_contract_prices +
 * gpl_contract_discount_rules onto it) — not on every recompute.
 *
 * priceBasis 'sheet' (ETC workbook rows): the stored contract_price IS
 * the MSRP — the workbook is a price *index*, and the discount-category
 * letter says which gpl_contract_discount_rules % applies at quote
 * time. So for 'sheet' rows the category rule is the ONLY path to a
 * contract price; without a rule the row is list-only. (NFR/LAB rows
 * resolve through their 0% rule: contract = list.) Mirrors
 * apps/backend/services/quotes.js resolvePriceRow — the two must agree.
 */
export function resolveUnitPrices(input: {
  msrp: number | null;
  contractPrice: number | null;
  discountPct: number | null;        // per-SKU stated discount
  categoryRulePct: number | null;    // gpl_contract_discount_rules fallback
  /** gpl_contract_prices.price_basis; default 'explicit' semantics. */
  priceBasis?: 'explicit' | 'sheet';
}): { listUnit: number | null; contractUnit: number | null; priceResolution: PriceResolution } {
  if (input.priceBasis === 'sheet') {
    const sheetMsrp = input.contractPrice ?? input.msrp;
    const listUnit = sheetMsrp !== null && Number.isFinite(sheetMsrp) ? round2(sheetMsrp) : null;
    if (listUnit !== null && input.categoryRulePct !== null && Number.isFinite(input.categoryRulePct)) {
      assertPct(input.categoryRulePct, 'categoryRulePct');
      return { listUnit, contractUnit: round2(listUnit * (1 - input.categoryRulePct)), priceResolution: 'category-rule' };
    }
    return { listUnit, contractUnit: null, priceResolution: 'list-only' };
  }

  const listUnit = input.msrp !== null && Number.isFinite(input.msrp) ? round2(input.msrp) : null;

  if (input.contractPrice !== null && Number.isFinite(input.contractPrice)) {
    return { listUnit, contractUnit: round2(input.contractPrice), priceResolution: 'explicit' };
  }
  if (listUnit !== null && input.discountPct !== null && Number.isFinite(input.discountPct)) {
    assertPct(input.discountPct, 'discountPct');
    return { listUnit, contractUnit: round2(listUnit * (1 - input.discountPct)), priceResolution: 'discount-pct' };
  }
  if (listUnit !== null && input.categoryRulePct !== null && Number.isFinite(input.categoryRulePct)) {
    assertPct(input.categoryRulePct, 'categoryRulePct');
    return { listUnit, contractUnit: round2(listUnit * (1 - input.categoryRulePct)), priceResolution: 'category-rule' };
  }
  return { listUnit, contractUnit: null, priceResolution: 'list-only' };
}

/**
 * Which off-list lever (if any) covers a line, from its snapshotted
 * discount category. Letter semantics per gpl_contract_discount_rules
 * on every vehicle on file: A = product; B (Subscriptions), C
 * (Frontline Support), D (Backline Support), G (Backline Bundle),
 * L (Cloud Subscriptions) = services. E (Professional Services),
 * NFR-LAB, Spare, Marketplace, and uncategorized lines return null —
 * the levers never touch them (they price at contract).
 * Resolves ETC letters ('A'), SLP paren labels ('Hardware (A)'), and
 * SLP word labels ('Support').
 */
export function discountBucketOf(discountCategory: string | null | undefined): 'product' | 'services' | null {
  const c = String(discountCategory ?? '').trim().toUpperCase();
  if (!c) return null;
  const letter = /^[A-Z]$/.test(c) ? c : (c.match(/\(([A-Z])\)$/) ?? [])[1] ?? null;
  if (letter) {
    if (letter === 'A') return 'product';
    return ['B', 'C', 'D', 'G', 'L'].includes(letter) ? 'services' : null;
  }
  if (/^(HARDWARE|PRODUCTS?)\b/.test(c)) return 'product';
  if (/^(SUPPORT|SUBSCRIPTIONS?|CLOUD)\b/.test(c)) return 'services';
  return null;
}

/**
 * A line's customer base unit BEFORE the legacy scenario discount and
 * per-line extra: its contract price, deepened to list × (1 − lever)
 * when the line's bucket lever is set and cuts deeper. Contract is the
 * ceiling — the lever can only lower the price, never raise it. The
 * single definition shared by the engine and the per-line "Cust %"
 * editor so typed percentages and slider math always agree.
 */
export function lineCustomerBaseUnit(
  line: Pick<QuoteLineItem, 'listUnit' | 'contractUnit' | 'discountCategory'>,
  scenario: QuoteScenario,
): number | null {
  if (line.contractUnit === null) return null;
  const contractUnit = round2(line.contractUnit);
  const bucket = discountBucketOf(line.discountCategory);
  const target = bucket === 'product'
    ? scenario.productOffListPct
    : bucket === 'services' ? scenario.servicesOffListPct : null;
  if (target == null || line.listUnit === null || line.listUnit <= 0) return contractUnit;
  return Math.min(contractUnit, round2(line.listUnit * (1 - target)));
}

/** Per-line economics under one scenario. */
export function computeLineEconomics(line: QuoteLineItem, scenario: QuoteScenario): LineEconomics {
  validateScenario(scenario);
  if (!Number.isFinite(line.qty) || line.qty < 0) {
    throw new RangeError(`qty must be a non-negative number, got ${line.qty} (line ${line.lineId})`);
  }
  if (line.additionalDiscountPct !== undefined) {
    assertPct(line.additionalDiscountPct, `additionalDiscountPct (line ${line.lineId})`);
  }

  // Per-year priced lines (annual unit price, term chosen per deal —
  // Prisma Access etc.): every extended figure scales by term-years so
  // TCV, tax, and the waterfall all see the full-term cost. Unit
  // prices stay per-year (that's what the contract states).
  const termYearsFactor = line.pricedPerYear && line.termMonths && line.termMonths > 0
    ? line.termMonths / 12
    : 1;

  const listExtended = line.listUnit !== null ? round2(round2(line.listUnit) * line.qty * termYearsFactor) : null;
  const contractExtended = line.contractUnit !== null ? round2(round2(line.contractUnit) * line.qty * termYearsFactor) : null;

  let customerUnit: number | null = null;
  let customerExtended: number | null = null;
  const baseUnit = lineCustomerBaseUnit(line, scenario);
  if (baseUnit !== null) {
    // Bucket off-list lever sets the base (contract is the ceiling);
    // the legacy scenario discount and per-line extra compound on top.
    const extra = line.additionalDiscountPct ?? 0;
    customerUnit = round2(baseUnit * (1 - scenario.customerDiscountPct) * (1 - extra));
    customerExtended = round2(customerUnit * line.qty * termYearsFactor);
  }

  // Per-line % off list, both faces of the deal. The internal (deal
  // desk) % walks this line's customer price through the channel — it
  // moves automatically whenever the customer % moves. PAN-paid
  // financing deepens it too (same simple-interest factor as the
  // quote-level ask: panNet × (1 − rate × years)) so the Ask % column
  // and the headline ask always agree; customer-paid financing rides
  // the customer total instead and stays out.
  let customerOffListPct: number | null = null;
  let panOffListPct: number | null = null;
  if (customerUnit !== null && line.listUnit !== null && line.listUnit > 0) {
    customerOffListPct = round4(1 - customerUnit / line.listUnit);
    // Additive channel model: partner + disti margins are points off
    // LIST stacked on the customer discount (25 + 8 + 2 = 35).
    const financeFactor = (scenario.financePayer ?? 'pan') === 'pan'
      ? 1 - (scenario.financeRatePct ?? 0) * (scenario.financeYears ?? 0)
      : 1;
    const panNetUnit = (customerUnit - line.listUnit * (scenario.partnerMarginPct + scenario.distiMarginPct))
      * (1 - (scenario.panDealRegPct ?? 0))
      * financeFactor;
    panOffListPct = round4(1 - panNetUnit / line.listUnit);
  }

  // Term view: a line with a term annualizes to its ACV contribution
  // (a 6-month term costs ×2 per year). One-time lines stay null.
  const termMonths = Number.isFinite(line.termMonths as number) && (line.termMonths as number) > 0
    ? (line.termMonths as number)
    : null;
  const annualizedCustomer = termMonths !== null && customerExtended !== null
    ? round2(customerExtended * (12 / termMonths))
    : null;

  return {
    lineId: line.lineId,
    listExtended,
    contractExtended,
    customerUnit,
    customerExtended,
    priceResolution: line.contractUnit === null ? 'list-only' : line.priceResolution,
    customerOffListPct,
    panOffListPct,
    termMonths,
    annualizedCustomer,
  };
}

/** Whole-quote waterfall under one scenario. */
export function computeQuoteEconomics(
  lines: QuoteLineItem[],
  scenario: QuoteScenario,
  opts?: {
    /**
     * CA sales-tax placeholder rate (fraction). When given, estimatedTax
     * = Σ taxable lines' customer extended × rate. Lines with taxable
     * null/undefined are treated as NOT taxable (CA taxes tangible
     * property; most of the catalog — electronic software, subs,
     * support, services — is exempt).
     */
    taxRatePct?: number | null;
  },
): QuoteEconomics {
  validateScenario(scenario);
  const taxRate = opts?.taxRatePct ?? null;
  if (taxRate !== null) assertPct(taxRate, 'taxRatePct');

  // Organizer rows (headers/sections) are not products — excluded from
  // all math. Toggled-off sections/groups/lines stay in lineEconomics
  // (the UI still shows their numbers, dimmed) but contribute NOTHING
  // to any total.
  const flags = segmentEnabled(lines);
  const realIdx = lines.map((l, i) => ({ l, i })).filter(({ l }) => !isOrganizerLine(l));
  const realLines = realIdx.map(({ l }) => l);
  const realOn = realIdx.map(({ i }) => flags[i]);
  const lineEconomics = realLines.map((l) => computeLineEconomics(l, scenario));

  const sum = (vals: Array<number | null>): number =>
    round2(vals.reduce<number>((acc, v) => acc + (v ?? 0), 0));
  const sumOn = (pick: (le: LineEconomics) => number | null): number =>
    sum(lineEconomics.map((le, i) => (realOn[i] ? pick(le) : null)));

  const listTotal = sumOn((l) => l.listExtended);
  const contractTotal = sumOn((l) => l.contractExtended);
  const customerBase = sumOn((l) => l.customerExtended);

  // Financing: 'pan' (default) accrues on PAN net and deepens the
  // internal ask — invisible to the customer. 'customer' adds it to the
  // customer-facing total (thin-margin deals); channel prices and the
  // ask are computed from the BASE — financing passes through.
  const payer = scenario.financePayer ?? 'pan';
  const rate = scenario.financeRatePct ?? 0;
  const years = scenario.financeYears ?? 0;
  const customerFinancing = payer === 'customer' ? round2(customerBase * rate * years) : 0;
  const customerTotal = round2(customerBase + customerFinancing);

  // CHANNEL MODEL (Ryan, 2026-07-03): partner and disti margins are
  // ADDITIVE POINTS OFF LIST — "sell the customer at 25 off, partner
  // wants 8, disti takes 2 → ask Palo Alto for 35." The disti cut
  // stacks with the partner's (it comes out of the channel ask); it is
  // NOT a second multiplicative slice below partner buy. Margin base is
  // LIST, falling back to the customer base when no line carries MSRP.
  const marginBase = listTotal > 0 ? listTotal : customerBase;
  const partnerBuyTotal = round2(customerBase - marginBase * scenario.partnerMarginPct);
  const distiBuyTotal = round2(partnerBuyTotal - marginBase * scenario.distiMarginPct);
  const panNetTotal = round2(distiBuyTotal * (1 - (scenario.panDealRegPct ?? 0)));

  const partnerMargin = round2(customerBase - partnerBuyTotal);
  const distiMargin = round2(partnerBuyTotal - distiBuyTotal);
  const channelMargin = round2(customerBase - distiBuyTotal);

  const pctOf = (amount: number, base: number): number => (base > 0 ? round4(amount / base) : 0);

  const savingsVsListAmount = round2(listTotal - customerTotal);
  const financingCost = payer === 'customer'
    ? customerFinancing
    : round2(panNetTotal * rate * years);

  // Estimated CA sales tax (placeholder): taxable lines only (hardware;
  // per tax_rules snapshot on each line), on the customer price. When
  // financing is customer-paid it's a finance charge, not a sale of
  // property — the taxable base stays the pre-financing amount.
  const taxableBase = taxRate !== null
    ? sum(realLines.map((l, i) => (realOn[i] && l.taxable ? lineEconomics[i].customerExtended : null)))
    : 0;
  const estimatedTax = taxRate !== null ? round2(taxableBase * taxRate) : 0;

  // Term view (TCV/ACV). ACV = Σ annualized term lines; one-time =
  // priced lines without a term. TCO horizons assume renewal at
  // snapshot pricing and exclude tax + financing (see computeTcoRoll
  // for the per-year roll with explicit assumptions).
  // Parked (qty-0) lines contribute $0 and are excluded from exports —
  // keep them out of the COUNTS too, or labels like "N recurring lines"
  // and the unpriced warning contradict what actually prints.
  const quoted = (i: number): boolean => realOn[i] && realLines[i].qty > 0;
  const termLineCount = lineEconomics.filter((le, i) => quoted(i) && le.annualizedCustomer !== null).length;
  const acvCustomer = sumOn((l) => l.annualizedCustomer);
  const oneTimeCustomerTotal = sum(
    lineEconomics.map((le, i) => (realOn[i] && le.termMonths === null ? le.customerExtended : null)),
  );

  const marginPct = (amount: number): number => (marginBase > 0 ? round4(amount / marginBase) : 0);

  return {
    scenario: scenario.name,
    currency: 'USD',
    lineCount: realOn.filter(Boolean).length,
    unpricedLineCount: lineEconomics.filter((l, i) => quoted(i) && l.priceResolution === 'list-only').length,
    excludedLineCount: realOn.filter((on) => !on).length,
    listTotal,
    contractTotal,
    customerTotal,
    partnerBuyTotal,
    distiBuyTotal,
    panNetTotal,
    savingsVsListAmount,
    savingsVsListPct: pctOf(savingsVsListAmount, listTotal),
    // Financing (simple: customer total × rate × years) comes out of the
    // internal ask — PAN funds it via a deeper approved discount.
    financingCost,
    estimatedTax,
    taxRatePct: taxRate,
    taxableBase,
    customerTotalWithTax: round2(customerTotal + estimatedTax),
    panDiscountVsListPct: listTotal > 0
      ? round4(1 - (panNetTotal - (payer === 'pan' ? financingCost : 0)) / listTotal)
      : 0,
    // Margin pcts are POINTS OF LIST (the margin base) so they read
    // back exactly as the levers were set: 8 means "8 off list".
    marginByTier: {
      partner: { amount: partnerMargin, pct: marginPct(partnerMargin) },
      disti: { amount: distiMargin, pct: marginPct(distiMargin) },
      channelTotal: { amount: channelMargin, pct: marginPct(channelMargin) },
    },
    annualPaymentCustomer: years > 0 ? round2(customerTotal / years) : null,
    termLineCount,
    oneTimeCustomerTotal,
    acvCustomer,
    tco3yCustomer: round2(oneTimeCustomerTotal + acvCustomer * 3),
    tco5yCustomer: round2(oneTimeCustomerTotal + acvCustomer * 5),
    lines: lineEconomics,
  };
}

/**
 * Customer-price TCO roll over a horizon (C3). One-time lines land in
 * year 1; term lines annualize (extended ÷ term years) and renew at
 * snapshot pricing every year of the horizon. Unpriced (list-only)
 * lines are EXCLUDED and counted, never silently absorbed. Tax and
 * financing stay out — this is the comparable-cost view, and the
 * assumptions ride along so no consumer can present the number bare.
 */
export function computeTcoRoll(
  lines: QuoteLineItem[],
  scenario: QuoteScenario,
  opts?: { horizonYears?: number },
): TcoRoll {
  const horizonYears = Math.max(1, Math.round(opts?.horizonYears ?? 5));
  const flags = segmentEnabled(lines);
  const real = lines
    .map((l, i) => ({ l, on: flags[i] }))
    .filter(({ l, on }) => !isOrganizerLine(l) && on);
  const econ = real.map(({ l }) => computeLineEconomics(l, scenario));

  const sum = (vals: Array<number | null>): number =>
    round2(vals.reduce<number>((acc, v) => acc + (v ?? 0), 0));
  const oneTimeTotal = sum(econ.map((le) => (le.termMonths === null ? le.customerExtended : null)));
  const recurringAnnual = sum(econ.map((le) => le.annualizedCustomer));
  // Parked (qty-0) lines contribute $0 — keep them out of the counts
  // and the unpriced caveat (they're deliberately not being quoted).
  const termLineCount = econ.filter((le, i) => real[i].l.qty > 0 && le.annualizedCustomer !== null).length;
  const unpricedLineCount = econ.filter((le, i) => real[i].l.qty > 0 && le.priceResolution === 'list-only').length;

  const years = Array.from({ length: horizonYears }, (_, k) => {
    const oneTime = k === 0 ? oneTimeTotal : 0;
    return { year: k + 1, oneTime, recurring: recurringAnnual, total: round2(oneTime + recurringAnnual) };
  });

  const assumptions = [
    'Term lines annualized (customer extended ÷ term years) and assumed to renew at snapshot pricing through the horizon.',
    'One-time lines (hardware, optics, services) land in year 1; no hardware refresh is modeled inside the horizon.',
    'Customer pricing under the active scenario; excludes estimated tax and financing.',
  ];
  if (unpricedLineCount > 0) {
    assumptions.push(`${unpricedLineCount} unpriced (list-only) line${unpricedLineCount === 1 ? '' : 's'} excluded from the roll.`);
  }

  return {
    scenario: scenario.name,
    horizonYears,
    years,
    oneTimeTotal,
    recurringAnnual,
    horizonTotal: round2(oneTimeTotal + recurringAnnual * horizonYears),
    termLineCount,
    unpricedLineCount,
    assumptions,
  };
}

/** Fraction rounding for percentage displays (4 dp ⇒ 0.01% precision). */
function round4(n: number): number {
  return Math.round(Number((n * 10000).toPrecision(15))) / 10000;
}
